// Adventures of Black Emigo — achievements + daily-streak tracking
// All persistence in localStorage. No backend dependency.
//
// Storage rationale: this is single-player game progress with no PII, no auth
// tokens, and no sensitive data. localStorage is the correct layer because:
//   * The data MUST persist across sessions (streaks, unlocks) — rules out sessionStorage.
//   * There is no user identity to sync — rules out server-side/cookies.
//   * XSS on a static game with no auth has no privileged surface to exploit.
// If we ever add authenticated accounts, this would move to the backend.

const UNLOCKED_KEY = "aobe_achievements";
const DAILY_HISTORY_KEY = "aobe_daily_history";

export const ACHIEVEMENTS = [
  {
    id: "first-run",
    label: "First Run",
    desc: "Submit your first score.",
    iconKey: "flag",
    color: "#39ff14",
  },
  {
    id: "first-boss",
    label: "First Boss Down",
    desc: "Defeat your first mini-boss (SWAT van or chopper).",
    iconKey: "skull",
    color: "#ff003c",
  },
  {
    id: "boss-breaker",
    label: "Bossbreaker",
    desc: "Defeat 3 bosses in a single run.",
    iconKey: "crosshair",
    color: "#ff003c",
  },
  {
    id: "coin-100",
    label: "100 Coin Run",
    desc: "Collect 100 coins in a single run.",
    iconKey: "coins",
    color: "#ffcc00",
  },
  {
    id: "power-trio",
    label: "Power Trio",
    desc: "Pick up all three power-up types in a single run.",
    iconKey: "zap",
    color: "#ffcc00",
  },
  {
    id: "daily-streak-3",
    label: "Daily Streak ×3",
    desc: "Complete the daily run 3 days in a row.",
    iconKey: "flame",
    color: "#ff003c",
  },
  {
    id: "daily-streak-7",
    label: "Daily Streak ×7",
    desc: "Complete the daily run 7 days in a row.",
    iconKey: "flame-double",
    color: "#ffcc00",
  },
];

import { devWarn } from "@/lib/devLog";

function safeRead(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch (e) {
    // localStorage may be unavailable (private mode) or hold malformed JSON.
    // Falling back to a fresh value is the right thing for the player.
    devWarn(`achievements: failed to read ${key}`, e);
    return fallback;
  }
}
function safeWrite(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // Quota or disabled storage — surface to console but don't break the game.
    devWarn(`achievements: failed to persist ${key}`, e);
  }
}

// ----- Unlocks -----
export function getUnlockedMap() {
  return safeRead(UNLOCKED_KEY, {}); // { id: ISOdate }
}
export function isUnlocked(id) {
  return !!getUnlockedMap()[id];
}
export function unlock(id) {
  const map = getUnlockedMap();
  if (map[id]) return false;
  map[id] = new Date().toISOString();
  safeWrite(UNLOCKED_KEY, map);
  return true;
}

// ----- Daily history & streak -----
export function getDailyHistory() {
  const arr = safeRead(DAILY_HISTORY_KEY, []);
  return Array.isArray(arr) ? arr : [];
}

function dateAddDays(yyyyMmDd, delta) {
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

export function recordDailyCompletion(seedDate) {
  if (!seedDate) return;
  const history = new Set(getDailyHistory());
  history.add(seedDate);
  safeWrite(DAILY_HISTORY_KEY, Array.from(history).sort());
}

/** Streak ending today (or yesterday if today not yet done). */
export function getStreak(todayString) {
  const set = new Set(getDailyHistory());
  if (set.size === 0) return 0;
  // Anchor: if today completed, start from today; else if yesterday completed, start from yesterday; else 0
  let anchor = null;
  if (set.has(todayString)) anchor = todayString;
  else {
    const yesterday = dateAddDays(todayString, -1);
    if (set.has(yesterday)) anchor = yesterday;
  }
  if (!anchor) return 0;
  let streak = 0;
  let cursor = anchor;
  while (set.has(cursor)) {
    streak++;
    cursor = dateAddDays(cursor, -1);
  }
  return streak;
}

// ----- Evaluator helpers (one per rule) -----
function checkParticipation(stats) {
  return stats.score >= 0 ? ["first-run"] : [];
}
function checkBossAchievements(stats) {
  const out = [];
  if (stats.bossesDefeated >= 1) out.push("first-boss");
  if (stats.bossesDefeated >= 3) out.push("boss-breaker");
  return out;
}
function checkCoinAchievements(stats) {
  return stats.coinsCollected >= 100 ? ["coin-100"] : [];
}
function checkPowerupAchievements(stats) {
  const p = stats.powerupsByType || {};
  const trio = (p.chain || 0) > 0 && (p.shades || 0) > 0 && (p.cup || 0) > 0;
  return trio ? ["power-trio"] : [];
}
function checkDailyStreakAchievements(stats) {
  if (stats.mode !== "daily" || !stats.seedDate) return [];
  recordDailyCompletion(stats.seedDate);
  const streak = getStreak(stats.todayString || stats.seedDate);
  const out = [];
  if (streak >= 3) out.push("daily-streak-3");
  if (streak >= 7) out.push("daily-streak-7");
  return out;
}

const RULES = [
  checkParticipation,
  checkBossAchievements,
  checkCoinAchievements,
  checkPowerupAchievements,
  checkDailyStreakAchievements,
];

/**
 * Inspect a finished run and unlock any achievements newly earned.
 * @returns {string[]} list of newly unlocked achievement IDs.
 */
export function evaluateRun(stats) {
  const normalized = {
    score: 0,
    coinsCollected: 0,
    bossesDefeated: 0,
    powerupsByType: { chain: 0, shades: 0, cup: 0 },
    mode: "endless",
    seedDate: null,
    todayString: null,
    ...stats,
  };
  const newlyUnlocked = [];
  for (const rule of RULES) {
    for (const id of rule(normalized)) {
      if (unlock(id)) newlyUnlocked.push(id);
    }
  }
  return newlyUnlocked;
}

export function getAchievementById(id) {
  return ACHIEVEMENTS.find((a) => a.id === id);
}
